<template>
  <div
    id="ai-assistant-container"
    class="watson-compatible-container"
    :class="[aiAssistantClass, displayModeClass, { 'popover-centered': shouldHaveBlackOut }]"
    v-if="hasEnabledSeat"
  >
    <!-- FAB with tooltip - v-if wraps entire tooltip to prevent orphaned vnode -->
    <w-tooltip v-if="showFab" :content="$t('fab-tooltip')" position="top">
      <AIAssistantFab
        id="ai-assistant-fab"
        :opened="fabOpened"
        :active="attendanceStarted"
        :is-transitioning="false"
        :is-processing-minimized="isProcessingMinimized"
        :is-remote-recording="isRemoteRecording || showDeviceRecordingWidget"
        @click="handleFabClick"
        @finalize-recording="handleFinalizeRecording"
        :class="{ marginChat: true }"
        v-bind="props"
      />
    </w-tooltip>

    <!-- Processing State - centered modal with spinner -->
    <!-- v-if controls existence (state), v-show controls visibility (minimize) -->
    <AIAssistantProcessingView
      v-if="currentState === 'processing'"
      v-show="!machine.minimized"
      :session-data="reactiveSessionData"
      :is-polling-active="isPollingActive"
      @minimize="onMinimize"
    />

    <!-- DIRECT TEST: Simple div with v-if on currentState -->
    <!-- <div 
      v-if="currentState === 'review_ready'" 
      class="direct-test-element"
      style="position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); background: red; color: white; padding: 40px; font-size: 24px; z-index: 9999999999999; border-radius: 16px;"
    >
      ✅ DIRECT TEST: currentState IS review_ready!
    </div> -->

    <!-- INLINE TEST: Same structure as ReviewView but inline -->
    <!-- <div 
      v-if="currentState === 'review_ready'"
      class="inline-review-test"
      style="position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); background: white; padding: 30px; z-index: 9999999; border: 5px solid blue; border-radius: 16px; box-shadow: 0 10px 30px rgba(0,0,0,0.3);"
    >
      <h2 style="color: blue; margin: 0 0 20px 0;">📝 INLINE REVIEW TEST</h2>
      <p style="margin: 0;">This is an inline div (no component import)</p>
      <p style="margin: 10px 0 0 0; color: green;">If you see this but not ReviewView, the bug is in the component.</p>
    </div> -->

    <!-- Review State - using direct comparison like the working red box -->
    <!-- v-if controls existence (state), v-show controls visibility (minimize) -->
    <AIAssistantReviewView
      v-if="currentState === 'review_ready'"
      v-show="!machine.minimized"
      :session-data="reactiveSessionData"
      :display-mode="machine.displayMode"
      :position="props.position"
      :is-minimized="machine.minimized"
      @expand="machine.setDisplayMode('modal')"
      @minimize="onMinimize"
      @collapse="machine.setDisplayMode('widget')"
      @review-saved="onReviewSaved"
      @session-discarded="onSessionDiscarded"
    />

    <!-- Onboarding Modal (legacy - simplified) -->
    <OnboardingModal
      :is-open="showOnboardingModal"
      title="Agora você tem acesso ao NOA Notes"
      primary-button-label="Continuar"
      secondary-button-label="Lembrar mais tarde"
      terms-url="https://dp-one-terms-conditions.s3.eu-central-1.amazonaws.com/BR+Master+T%26Cs+-+Doctor+Copilot+2024+.docx.pdf"
      @close="handleOnboardingClose"
      @primary-button-click="handleOnboardingAccept"
      @secondary-button-click="handleOnboardingClose"
    />

    <!-- Consent Modal (legacy - simplified) -->
    <AIAssistantStart
      v-if="showConsentModal"
      :is-open="showConsentModal"
      @close="handleConsentClose"
      @start="handleConsentAccept"
    />

    <!-- Template Selection Modal -->
    <AIAssistantFormTemplateSelectionModal
      v-if="showFormTemplateSelection"
      :templates="machine.templates"
      :pre-selected-template-id="null"
      @select="handleFormTemplateSelected"
      @close="handleFormTemplateClose"
    />

    <!-- Device Selection Modal -->
    <AIAssistantDeviceSelectionModal
      v-if="showDeviceSelection"
      :is-open="showDeviceSelection"
      :session-data="machine.sessionData"
      @close="handleDeviceSelectionClose"
      @select-desktop="handleDeviceSelectDesktop"
      @select-mobile="handleDeviceSelectMobile"
    />

    <!-- Device Recording Widget (shown when recording started on mobile device) -->
    <AIAssistantDeviceRecordingWidget :visible="showDeviceRecordingWidget" />

    <!-- Recorder Modal -->
    <AIAssistantTranscriptionModal
      v-if="showRecording"
      ref="recorderModalRef"
      :is-open="machine.recorderVisible"
      :iframe-url="machine.recorderUrl"
      :iframe-loaded="recorderLoaded"
      :show-skeleton="!recorderLoaded && recorderLoading"
      :iframe-error="recorderError"
      :iframe-key="recorderKey"
      :session-id="machine.sessionData?.recorderSessionId"
      @close="handleRecorderClose"
      @modal-opened="onRecorderOpened"
      @modal-closed="onRecorderClosed"
      @recorder-ready="onRecorderReady"
      @recording-started="onRecordingStarted"
      @recording-finished="handleRecordingFinished"
      @iframe-load="onIframeLoad"
      @iframe-load-start="onIframeLoadStart"
      @iframe-error="onIframeError"
      @start-new-session="handleStartNewSessionFromRecorder"
    />

    <!-- Empty Summary Modal (shown when transcription was too short) -->
    <w-modal
      :model-value="showEmptySummaryModal"
      @update:model-value="
        (val) => {
          if (!val) handleEmptySummaryClose();
        }
      "
      size="medium"
      title="Transcrição insuficiente"
      :close-on-click-outside="false"
      style="z-index: 999999999"
      class="empty-summary-modal"
      @close="handleEmptySummaryClose"
    >
      <template #body>
        <div style="padding: var(--w-space-sm)">
          <w-text type="body" tag-type="p">
            Não houve transcrição suficiente para gerar seu resumo. Por favor, tente novamente com uma gravação mais
            longa.
          </w-text>
        </div>
      </template>
      <template #footer>
        <div style="display: flex; justify-content: flex-end; padding: var(--w-space-sm)">
          <w-button label="Entendi" variant="primary" @click="handleEmptySummaryClose" />
        </div>
      </template>
    </w-modal>

    <!-- Interrupted Session Modal (shown when recorder connection was lost unexpectedly) -->
    <w-modal
      :model-value="showInterruptedSessionModal"
      @update:model-value="(val) => { if (!val) handleInterruptedSessionClose(); }"
      size="medium"
      title="Conexão com o gravador perdida"
      :close-on-click-outside="false"
      style="z-index: 999999999"
      @close="handleInterruptedSessionClose"
    >
      <template #body>
        <div style="padding: var(--w-space-sm)">
          <w-text type="body" tag-type="p">
            A conexão com o gravador foi perdida inesperadamente. A transcrição da sessão estará disponível em até 1
            hora.
          </w-text>
        </div>
      </template>
      <template #footer>
        <div style="display: flex; justify-content: flex-end; padding: var(--w-space-sm)">
          <w-button label="Entendi" variant="primary" @click="handleInterruptedSessionClose" />
        </div>
      </template>
    </w-modal>

    <!-- Transcription Text Modal -->
    <w-modal
      :model-value="transcriptionTextOpen"
      @update:model-value="
        (val) => {
          if (!val) transcriptionTextOpen = false;
        }
      "
      size="large"
      class="transcription-text-modal"
      title="Transcrição"
      :close-on-click-outside="false"
      style="z-index: 999999999"
      @close="transcriptionTextOpen = false"
    >
      <template #body>
        <div class="transcription-text-container">
          <div class="badge-container">
            <div class="memo-header">
              <div class="actions-wrapper">
                <w-button
                  variant="plain"
                  label="Copiar"
                  leading-icon="clipboard"
                  @click="handleCopyTranscription"
                  class="copy-button"
                />
              </div>
            </div>
          </div>
          <w-textarea
            :key="`transcription-${transcriptionTextContent.length}`"
            :model-value="transcriptionTextContent"
            :rows="20"
            readonly
            placeholder="A transcrição aparecerá aqui..."
            class="transcription-textarea"
          />
        </div>
        <w-toast :toasts="transcriptionToasts" />
      </template>
    </w-modal>

    <!-- Global toast for messages that need to persist after component unmount -->
    <w-toast :toasts="globalToasts" />
  </div>
</template>

<style scoped lang="scss">
.w-tooltip-popout {
  text-align: center !important;
}

#ai-assistant-container {
  --footer-height: 64px;
  position: fixed;
  bottom: var(--footer-height);
  z-index: 890;

  &.left {
    left: 30px;

    #ai-assistant-fab {
      left: 0;
      transform-origin: left center;
    }
  }

  &.right {
    right: 30px;

    #ai-assistant-fab {
      right: 0;
      transform-origin: right center;
    }

    .marginChat {
      margin-right: 60px;
    }
  }

  &.ai-widget {
    bottom: 35px;
  }

  &.ai-modal:after {
    background-color: var(--w-modal-backdrop-color-background);
    content: "";
    pointer-events: none;
    z-index: -1;
    display: flex;
    height: 100%;
    left: 0;
    position: fixed;
    top: 0;
    width: 100%;
    visibility: visible;
    opacity: 0;
    transition: opacity 0.4s ease-in-out;
  }

  &.ai-modal.opened:after {
    opacity: 1;
  }

  .center-popover {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    margin: 0;
  }
}

.transcription-text-modal {
  .transcription-text-container {
    padding: var(--w-space-m);
  }

  .transcription-textarea {
    width: 100%;
    min-height: 400px;
    font-family: monospace;
    font-size: 14px;
    line-height: 1.6;
  }
}
</style>

<style>
#ai-assistant-container .w-popover-popout {
  z-index: 10000000000;
}

.opened.popover-centered::after {
  opacity: 1 !important;
}

.transcription-text-modal .transcription-text-container {
  padding: var(--w-space-m);
}

.transcription-text-modal .badge-container {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 8px;
}

.transcription-text-modal .memo-header {
  display: flex;
  align-items: center;
}

.transcription-text-modal .actions-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 4px 0 4px 0;
}

.transcription-text-modal .copy-button {
  font-size: 13px;
  padding: 4px 12px;
}

.transcription-text-modal .copy-button .w-icon {
  width: 14px;
  height: 14px;
}

.transcription-text-modal .transcription-textarea {
  width: 100%;
  min-height: 400px;
  font-family: monospace;
  font-size: 14px;
  line-height: 1.6;
}
</style>

<script setup lang="ts">
import AIAssistantFab from "@/components/ai-assistant/AIAssistantFab.vue";
import AIAssistantProcessingView from "@/components/ai-assistant/AIAssistantProcessingView.vue";
import AIAssistantReviewView from "@/components/ai-assistant/AIAssistantReviewView.vue";
import AIAssistantAttendanceStartCTA from "@/components/ai-assistant/AIAssistantAttendanceStartCTA.vue";
import AIAssistantTranscriptionModal from "@/components/ai-assistant/AIAssistantTranscriptionModal.vue";
import AIAssistantFormTemplateSelectionModal from "@/components/ai-assistant/AIAssistantFormTemplateSelectionModal.vue";
import AIAssistantDeviceSelectionModal from "@/components/ai-assistant/AIAssistantDeviceSelectionModal.vue";
import AIAssistantDeviceRecordingWidget from "@/components/ai-assistant/AIAssistantDeviceRecordingWidget.vue";
import AIAssistantStart from "@/components/ai-assistant/AIAssistantStart.vue";
import OnboardingModal from "@/components/ai-assistant/OnboardingModal.vue";
import WatsonComponentsCompatible from "@/components/watson/WatsonComponentsCompatible";
import { FetchApi } from "@/utils/FetchApi";
import { UserTracking } from "@/utils/UserTracking";
import { useStateMachine, type SessionData, type AIAssistantState } from "@/composables/useStateMachine";
import { useSessionApi } from "@/composables/useSessionApi";
import { useTemplateStore } from "@/composables/useTemplateStore";
import { useWebSocket, type TranscriptionStatusPayload } from "@/composables/useWebSocket";
import { useLongPolling } from "@/composables/useLongPolling";
import { useStatusTracking } from "@/composables/useStatusTracking";
import { useGrowthBook } from "@/composables/useGrowthBook";
import {
  NoaRecorderLifecycleTelemetry,
  createRecorderLifecycleApiSender,
  NOA_RECORDER_LIFECYCLE_FLAG_KEY,
} from "@/composables/useNoaRecorderLifecycleTelemetry";
import { useBrowserLifecycle } from "@/composables/useBrowserLifecycle";
import { useRecorderTab } from "@/composables/useRecorderTab";
import { computed, onMounted, provide, ref, h, createApp, onUnmounted, watch, nextTick } from "vue";
import { isLocalEnv, isStagingEnv } from "@/utils/IdentifyGlobalEnv";
import { useSessionStore } from "@/composables/useSessionStore";
import { formatSummaryData } from "@/utils/formatters";
import {
  createNoaObservabilityBridgeSender,
  setNoaRumSessionContext,
  clearNoaRumSessionContext,
  setNoaTenantContext,
  trackNoaRumAction,
  NOA_RUM_EVENT,
} from "@/utils/noaObservability";
import { watchAndInitObservabilityWhenFlagOn } from "@/utils/observability";

// =============================================================================
// Types
// =============================================================================

interface Config {
  autoStart?: boolean;
}

interface Props {
  position: "left" | "right";
  token: string | null;
  userId: string | null;
  patientId: string | null;
  bookingId: string | null;
  licenseId: string | null;
  pms: string | null;
  professionalId: string | null;
  config?: Config;
}

interface SeatStatusResponse {
  enabled: boolean;
  isVip: boolean;
}

declare global {
  interface Document {
    userProperties: {
      idInTable: string;
      usuarioID: string;
      email: string;
    };
  }
}

// =============================================================================
// Props & State
// =============================================================================

const props = defineProps<Props>();
const emit = defineEmits(["close", "change-display-mode", "popover-centered"]);

// Create and provide apiClient BEFORE initializing state machine
// This ensures apiClient is available when useStateMachine calls useSessionApi
const apiClient = new FetchApi(process.env.VUE_APP_API_URL as string, { "x-access-token": props.token || "" });
provide("apiClient", apiClient);

// Initialize new state machine
const machine = useStateMachine();
const sessionStore = useSessionStore();

// Tab visibility + pagehide tracking for session-failure correlation
useBrowserLifecycle();

// GrowthBook feature flags
const { setAttributes, initGrowthBook, isOn } = useGrowthBook();
const newTabRecorderEnabled = isOn("NOA_new_tab_recorder_enabled");

// Initialize GrowthBook with user context from props.
// Experiment exposure is reported by the SDK trackingCallback: it only fires
// when an Experiment rule actually evaluated, so an empty features payload
// produces no event instead of a mislabeled one. The legacy watch-based
// experiment_assigned events below stay during the transition window.
initGrowthBook(
  {
    tenantId: props.licenseId || undefined,
    userId: props.userId || undefined,
  },
  (assignment) => {
    UserTracking.trackEvent("experiment_assigned", {
      ...assignment,
      payload: {
        licenseId: props.licenseId,
        userId: props.userId,
      },
      pageUrl: window.location.href,
    });
  },
);

// Gate Datadog browser observability behind a GrowthBook kill switch + sample
// rate. Default = off (no SDK init, no billing). Call AFTER initGrowthBook
// so the watcher sees `isReady` flip true once features land.
watchAndInitObservabilityWhenFlagOn();

const recorderLifecycleTracingEnabled = isOn(NOA_RECORDER_LIFECYCLE_FLAG_KEY);

// A/B test: form_template_selection vs template_selection
// Watches the GrowthBook flag reactively — before GB loads isOn() returns false,
// so the old flow is used until the real value arrives.
// const showFormTemplateFlag = isOn("NOA_form_template_selection");
// watch(
//   showFormTemplateFlag,
//   (enabled) => {
//     machine.setEnableFormTemplateSelection(enabled);
//   },
//   { immediate: true },
// );


// Experiment exposure (experiment_assigned) is reported exclusively by the
// SDK trackingCallback registered in initGrowthBook above. It fires for any
// flag running as an Experiment rule in the GrowthBook panel — no per-flag
// tracking code needed. Do not add watch-based tracking: deriving the variant
// from a flag's boolean value mislabels default values as the control arm
// whenever the features payload fails to load.

// Component-specific refs
const attendanceStarted = ref(false);
const showNoaButton = ref(true);
const hasEnabledSeat = ref(false);
const iframeLoadTimeout = ref<ReturnType<typeof setTimeout> | null>(null);
const isResettingIframe = ref(false);
const recorderModalRef = ref<any>(null);
// displayMode, minimized, recorderVisible are now in useStateMachine
const isPopoverCentered = ref(false);
const professionalIdFromUserProperties = ref(document?.userProperties ? document.userProperties.idInTable : null);
const feegowProfessionalId = ref(props.professionalId || professionalIdFromUserProperties.value);
const transcriptionToasts = ref<any[]>([]);
const globalToasts = ref<any[]>([]);

const { isTabOpen, tabClosedUnexpectedly, openTab, focusTab, watchNamedTab, tryFocusNamedTab, clearTab } = useRecorderTab();

// Legacy onboarding/consent modals (simplified - not part of state machine)
const showOnboardingModal = ref(false);
const showConsentModal = ref(false);
const showInterruptedSessionModal = ref(false);

// Recorder state (local to this component - recorderVisible now in useStateMachine)
const recorderLoaded = ref(false);
const recorderLoading = ref(false);
const recorderError = ref<string | null>(null);
const recorderKey = ref(0);

const recorderLifecycleTelemetry = new NoaRecorderLifecycleTelemetry({
  isEnabled: () => Boolean(recorderLifecycleTracingEnabled.value),
  getRecorderSessionId: () => machine.sessionData?.recorderSessionId || machine.sessionId,
  getPagePath: () => window.location.pathname,
  getStateSnapshot: () => ({
    stateMachineState: machine.state,
    recorderVisible: machine.recorderVisible,
    iframePresent: Boolean(machine.recorderUrl),
    iframeLoaded: recorderLoaded.value,
    iframeLoading: recorderLoading.value,
    iframeErrored: Boolean(recorderError.value),
  }),
  send: createNoaObservabilityBridgeSender(createRecorderLifecycleApiSender(apiClient)),
});

// Propagate the recorder session id to Datadog RUM as a global context property
// so every subsequent RUM action/error/resource event carries it. Mirrors the
// `@noa.session_id` span tag added on the v2-api side, enabling cross-layer
// filtering in DD APM/RUM by the same identifier.
watch(
  () => machine.sessionData?.recorderSessionId || machine.sessionId,
  (next, previous) => {
    if (next && next !== previous) {
      setNoaRumSessionContext(next);
    } else if (!next && previous) {
      clearNoaRumSessionContext();
    }
  },
  { immediate: true },
);

// Tenant identifiers are stable for the plugin lifetime (set once from
// data-* attributes by the host PMS). Watch with `immediate` so the context
// lands as soon as Vue commits the initial props, before the observability
// SDK finishes its GrowthBook-gated init — the Datadog SDK queues
// `setGlobalContextProperty` calls until init completes.
watch(
  () => [props.licenseId, feegowProfessionalId.value, props.pms],
  ([licenseId, professionalId, pms]) => {
    setNoaTenantContext({ licenseId, professionalId, pms });
  },
  { immediate: true },
);

const RECORDER_LIFECYCLE_FINISHED_STATUSES = ["recorded", "transcribing", "transcribed", "analyzing"];

// Transcription text modal state (local)
const transcriptionTextOpen = ref(false);
const transcriptionTextContent = ref("");

// =============================================================================
// WebSocket Connection (Parent Level)
// =============================================================================

/**
 * WebSocket status change handler at parent level
 * Uses the new state machine's handleRecorderStatus() for clean mapping
 */
const handleWebSocketStatusChange = (status: string, data: TranscriptionStatusPayload) => {
  const normalizedStatus = status?.toLowerCase() || "";

  console.log("[AIAssistant:WebSocket] Status received:", {
    rawStatus: status,
    normalizedStatus,
    data,
    currentState: machine.state,
  });

  if (normalizedStatus === "recording") {
    void recorderLifecycleTelemetry.markRecordingStarted("backend_status_fallback");
  }

  if (RECORDER_LIFECYCLE_FINISHED_STATUSES.includes(normalizedStatus)) {
    void recorderLifecycleTelemetry.markRecordingFinished();
  }

  if (normalizedStatus === "interrupted") {
    void recorderLifecycleTelemetry.emitInterruptedObserved();
  }

  // For summarized status, we need to fetch the actual data from API
  if (normalizedStatus === "summarized") {
    const sessionId = machine.sessionData?.recorderSessionId;
    if (sessionId && apiClient) {
      apiClient
        .get(`/session/${sessionId}`)
        .then(
          (response: {
            summaryData?: string;
            transcriptionData?: string;
            insufficientTranscription?: boolean;
            sessionStatus?: string;
          }) => {
            if (response.insufficientTranscription || response.sessionStatus === "discarded") {
              machine.send({ type: "WS_ERROR", error: "Não houve transcrição suficiente para gerar seu resumo." });
              return;
            }
            // Send the event with fetched data
            machine.send({
              type: "WS_SUMMARIZED",
              summaryData: response.summaryData || data.parsedSummary || "",
              transcriptionData:
                response.transcriptionData || (data.transcription ? data.transcription.join("\n") : ""),
            });
          },
        )
        .catch((error: Error) => {
          console.error("[AIAssistant:WebSocket] Failed to fetch summary:", error);
          // Fallback to WebSocket data
          machine.send({
            type: "WS_SUMMARIZED",
            summaryData: data.parsedSummary || "",
            transcriptionData: data.transcription ? data.transcription.join("\n") : "",
          });
        });
    } else {
      // No sessionId - use WebSocket data directly
      machine.handleRecorderStatus(normalizedStatus, {
        summaryData: data.parsedSummary,
        transcriptionData: data.transcription ? data.transcription.join("\n") : undefined,
      });
    }
  } else if (normalizedStatus === "verified" || normalizedStatus === "discarded") {
    void recorderLifecycleTelemetry.markRecordingFinished();

    // Terminal events: disconnect transport and clean up
    disconnectAllTransports();
    stopStatusPolling();
    sessionStore.clearSessionInfo();

    // If already in idle (SummaryFooter already transitioned), nothing more to do
    if (machine.isIn("idle")) return;

    // External verified (iPront) — need to save and transition
    if (normalizedStatus === "verified" && !sessionStore.getReloadTimeline() && !machine.getSummarySaving()) {
      const sessionId = machine.sessionData?.recorderSessionId;
      const summary = machine.sessionData?.summaryData || machine.sessionData?.parsedSummary || "";
      const formatSummary = formatSummaryData(summary);

      if (!sessionId || !summary) {
        console.error(
          "[AIAssistant:WebSocket] Cannot save summary on verified: missing sessionId or summary from state machine"
        );
        machine.send({ type: "WS_ERROR", error: "Erro ao salvar resumo: dados da sessão indisponíveis." });
      } else {
        apiClient
          .put(`/session/${sessionId}`, { summary: formatSummary, status: "Verified" })
          .then((response: { jsCommand?: string }) => {
            // Track BEFORE eval(jsCommand) — see docs/ehr/known-issues.md #10.
            UserTracking.trackEvent("noa_notes_added", {
              page_category: "noa notes",
              page_section: "",
            });
            trackNoaRumAction(NOA_RUM_EVENT.SUMMARY_ACCEPTED, {
              session_id: sessionId,
              save_path: "ws_fallback",
            });

            if (response.jsCommand) {
              sessionStore.setReloadTimeline(response.jsCommand);
              eval(response.jsCommand);
            }
            globalToasts.value = [
              {
                content: "Resumo salvo com sucesso!",
                variant: "default",
                timeOut: 3000,
              },
            ];
            machine.send({ type: "WS_VERIFIED" });
          })
          .catch((error: Error) => {
            console.error("[AIAssistant:WebSocket] Failed to fetch iPront on verified:", error);
            globalToasts.value = [
              {
                content: "Erro ao salvar resumo.",
                variant: "error",
                timeOut: 3000,
              },
            ];
            machine.send({ type: "WS_ERROR", error: "Erro ao buscar iPront." });
          });
      }
    } else {
      // SummaryFooter already saved, or discarded — just transition
      machine.handleRecorderStatus(normalizedStatus, {
        error: data.error,
      });
    }
  } else {
    // For all other statuses, use the state machine's handler
    machine.handleRecorderStatus(normalizedStatus, {
      error: data.error,
    });
  }
};

// Initialize WebSocket composable at parent level
// This ensures connection persists across state transitions
const {
  connect: connectWebSocket,
  disconnect: disconnectWebSocket,
  isConnected: wsConnected,
  connectionAttemptFailed: wsConnectionFailed,
  updateStatusChangeCallback,
} = useWebSocket(
  null, // Will connect when sessionData is available
  handleWebSocketStatusChange,
  false, // Don't auto-connect
  { onRoomJoined: () => stopStatusPolling(), getToken: () => props.token },
);

// =============================================================================
// Long Polling Transport (Feature Flag: NOA_long_polling)
// =============================================================================

const useLongPollingFlag = isOn("NOA_long_polling");

const {
  isConnected: lpConnected,
  connectionAttemptFailed: lpConnectionFailed,
  connect: connectLongPolling,
  disconnect: disconnectLongPolling,
  updateStatusChangeCallback: updateLpCallback,
  setAuthToken: setLpAuthToken,
} = useLongPolling(handleWebSocketStatusChange, {
  onConnected: () => stopStatusPolling(),
});

// Pass the auth token to the long polling composable
if (props.token) {
  setLpAuthToken(props.token);
}

/**
 * Disconnect whichever transport is active (socket or long polling).
 * Used at cleanup points (unmount, terminal states, session change).
 */
function disconnectAllTransports() {
  disconnectWebSocket();
  disconnectLongPolling();
}

// Unified transport status: combines WS and LP based on active flag
const transportConnected = computed(() =>
  useLongPollingFlag.value ? lpConnected.value : wsConnected.value,
);
const transportConnectionFailed = computed(() =>
  useLongPollingFlag.value ? lpConnectionFailed.value : wsConnectionFailed.value,
);

// =============================================================================
// WebSocket Polling Fallback
// =============================================================================

// Polling interval ref for fallback when WebSocket fails to connect
const statusPollingIntervalId = ref<ReturnType<typeof setInterval> | null>(null);

/**
 * Start polling the session API for status changes.
 * Used as fallback when WebSocket connection fails.
 */
// Statuses that indicate recording has ended and processing has begun
const PROCESSING_STATUSES = ["recorded", "transcribing", "transcribed", "analyzing"];

// Status tracking composable for polling fallback
const { trackStatus } = useStatusTracking();

const startStatusPolling = () => {
  const sessionId = machine.sessionId;
  if (!sessionId || statusPollingIntervalId.value) return;

  console.log("[AIAssistant:Polling] Starting status polling fallback for session:", sessionId);

  statusPollingIntervalId.value = setInterval(async () => {
    try {
      const response = (await apiClient.get(`/session/${sessionId}`)) as {
        recorderStatus?: string;
        summaryData?: string;
        transcriptionData?: string;
        error?: string;
        insufficientTranscription?: boolean;
      };
      const status = response.recorderStatus?.toLowerCase();

      console.log("[AIAssistant:Polling] Status poll result:", { sessionId, status });

      // Track status for analytics (polling may miss intermediate states)
      if (status) {
        trackStatus(status, sessionId);
      }

      if (status === "recording") {
        void recorderLifecycleTelemetry.markRecordingStarted("backend_status_fallback");
      }

      if (PROCESSING_STATUSES.includes(status || "")) {
        void recorderLifecycleTelemetry.markRecordingFinished();
      }

      // Simulate WebSocket events based on status
      // Any processing status means recording has ended - transition out of recording modal
      // if (PROCESSING_STATUSES.includes(status || "") && machine.isIn("analyzing")) {
      if (status === "analyzing") {
        console.log("[AIAssistant:Polling] Detected processing status, transitioning to processing:", status);
        machine.send({ type: "WS_RECORDED" });
      } else if (status === "summarized" || status === "emptysummaryverified") {
        // Don't stop polling yet - keep checking for verified status after user saves
        if (machine.isIn("processing") || machine.isIn("device_selection") || machine.isIn("idle")) {
          console.log("[AIAssistant:Polling] Detected summary ready, transitioning to review");

          if (response.insufficientTranscription) {
            // Use same Portuguese message as WebSocket flow for modal detection
            stopStatusPolling();
            machine.send({ type: "WS_ERROR", error: "Não houve transcrição suficiente para gerar seu resumo." });
          } else {
            machine.send({
              type: "WS_SUMMARIZED",
              summaryData: response.summaryData || "",
              transcriptionData: response.transcriptionData || "",
            });
          }
        }
        // Continue polling while in review_ready to detect verified status
      } else if (status === "verified" || status === "discarded") {
        console.log("[AIAssistant:Polling] Detected terminal status, cleaning up:", status);
        void recorderLifecycleTelemetry.markRecordingFinished();
        disconnectAllTransports();
        stopStatusPolling();
        sessionStore.clearSessionInfo();
        if (!machine.isIn("idle")) {
          machine.send({ type: "WS_VERIFIED" });
        }
      } else if (status === "error" || status === "interrupted") {
        console.log("[AIAssistant:Polling] Detected error/interrupted status");
        if (status === "interrupted") {
          void recorderLifecycleTelemetry.emitInterruptedObserved();
        }
        stopStatusPolling();
        machine.send({ type: "WS_ERROR", error: response.error || "Unknown error" });
      }
    } catch (error) {
      console.error("[AIAssistant:Polling] Polling error:", error);
    }
  }, 5000); // Poll every 5 seconds
};

/**
 * Stop the status polling interval.
 */
const stopStatusPolling = () => {
  if (statusPollingIntervalId.value) {
    console.log("[AIAssistant:Polling] Stopping status polling");
    clearInterval(statusPollingIntervalId.value);
    statusPollingIntervalId.value = null;
  }
};

/**
 * Computed: Is polling currently active (WebSocket fallback mode)
 */
const isPollingActive = computed(() => statusPollingIntervalId.value !== null);

// Provide WebSocket status to children (for Summary component)
provide("wsStatusCallback", handleWebSocketStatusChange);
provide("wsConnected", transportConnected);

// =============================================================================
// Computed Properties
// =============================================================================

// Local ref synced from state machine via watcher for guaranteed reactivity.
// Using a ref instead of computed because Vue cannot track getters.
const currentState = ref<AIAssistantState>(machine.stateRef.value);

// Helper to check if current state matches any of the given states (reactive version)
const isInState = (...states: string[]) => {
  return states.includes(currentState.value);
};

const displayModeClass = computed(() => `ai-${machine.displayMode}`);
const popoverOpenedClass = computed(() => (isInState("review_ready", "processing") ? "popover-opened" : ""));

const aiAssistantClass = computed(() => ({
  left: props.position === "left",
  right: props.position === "right",
  opened: isInState("review_ready", "processing") && !machine.minimized,
  closed: isInState("idle"),
}));

const shouldHaveBlackOut = computed(() => {
  // Show backdrop during processing states only when modal is displayed (not minimized)
  return isInState("processing") && machine.displayMode === "modal";
});

/** Map state to summaryStatus for backward compatibility with child components */
const mapReviewStatusToSummaryStatus = computed(() => {
  switch (currentState.value) {
    case "processing":
      return "processing";
    case "review_ready":
      return "ready";
    case "recording":
      return "recording";
    case "error":
      return "interrupted";
    default:
      return "initialized";
  }
});

// Reactive computed for template v-if conditions
const showProcessingView = computed(() => isInState("processing") && machine.displayMode === "modal");
const showReviewView = computed(() => isInState("review_ready"));
const showFormTemplateSelection = computed(() => isInState("form_template_selection") && machine.templates.length > 0);
const showDeviceSelection = computed(() => isInState("device_selection"));
const showDeviceRecordingWidget = computed(() => machine.deviceRecordingActiveRef.value);
const isRemoteRecording = computed(() => isInState("recording") && (isTabOpen.value || machine.isRecoveredSession));
const showRecording = computed(() => isInState("recording") && !isResettingIframe.value && !isTabOpen.value && !machine.isRecoveredSession);
const fabOpened = computed(() => isInState("review_ready", "processing"));
const isProcessingMinimized = computed(() => isInState("processing") && machine.minimized);

/** Reactive session data for passing to child components */
const reactiveSessionData = computed(() => machine.sessionDataRef.value);

/** Show empty summary modal when error state has the insufficient transcription message */
const showEmptySummaryModal = computed(() => {
  const isErrorState = currentState.value === "error";
  const errorMsg = machine.errorMessage;
  const hasInsufficientMsg = errorMsg.includes("transcrição suficiente");
  const shouldShow = isErrorState && hasInsufficientMsg;

  console.log("[AIAssistant:EmptySummaryModal] Computing showEmptySummaryModal:", {
    currentState: currentState.value,
    isErrorState,
    errorMessage: errorMsg,
    hasInsufficientMsg,
    shouldShow,
  });

  return shouldShow;
});

/**
 * Is the current UI minimized (hidden but state still active)?
 * When minimized, FAB should SHOW to allow recovery.
 */
const isMinimized = computed(() => {
  // Minimized when:
  // 1. In recording state but recorder modal is hidden
  if (isInState("recording") && !machine.recorderVisible) {
    return true;
  }
  // 2. In review_ready and user clicked minimize button
  if (isInState("review_ready") && machine.minimized) {
    return true;
  }
  // 3. In processing but minimized (either in widget mode or explicitly minimized)
  if (isInState("processing") && (machine.displayMode === "widget" || machine.minimized)) {
    return true;
  }
  return false;
});

/**
 * Should the FAB be visible?
 * - Show in idle state (to start the flow)
 * - Show when minimized (to allow recovery/reopening)
 * - Hide during active modal dialogs (template/device selection, recording modal, processing modal)
 */
const showFab = computed(() => {
  // Always show FAB when minimized (to allow recovery)
  if (isMinimized.value) {
    return true;
  }
  // Show FAB when recording is happening on mobile device
  if (showDeviceRecordingWidget.value) {
    return true;
  }
  // Hide FAB during modal dialogs (form_template/device selection)
  if (isInState("form_template_selection", "device_selection")) {
    return false;
  }
  // Show FAB when recording is happening in another tab
  if (isRemoteRecording.value) {
    return true;
  }
  // Hide FAB when recording modal is actively showing
  if (isInState("recording") && machine.recorderVisible) {
    return false;
  }
  // Hide FAB when processing/review modal is actively showing (modal mode)
  if (isInState("processing", "review_ready")) {
    return false;
  }
  // Show FAB in idle state
  return true;
});

// =============================================================================
// Seat Status
// =============================================================================

const checkSeatStatus = async () => {
  if (!props.licenseId || !feegowProfessionalId.value) {
    return;
  }

  const storageKey = `aiAssistantStatusResponse_${props.licenseId}_${feegowProfessionalId.value}`;
  const cachedStatus = sessionStorage.getItem(storageKey);
  const cachedTimeKey = `${storageKey}_lastCachedTime`;
  const cachedTime = sessionStorage.getItem(cachedTimeKey);

  if (cachedStatus && cachedTime) {
    try {
      const response: SeatStatusResponse = JSON.parse(cachedStatus);
      const lastCachedTimestamp = parseInt(cachedTime, 10);
      const cacheAgeMinutes = (Date.now() - lastCachedTimestamp) / (1000 * 60);

      if (cacheAgeMinutes < 60) {
        hasEnabledSeat.value = response.enabled || response.isVip;

        if (response.enabled) {
          UserTracking.identifyUser({
            pms_name: props.pms || "feegow",
            country_code: "br",
            pms_doctor_id: `${props.licenseId}-${feegowProfessionalId.value}`,
            user_type: "doctor",
          });
        }
        return;
      }
    } catch {
      // Ignore cache parse errors
    }
  }

  try {
    const response = await apiClient.get<SeatStatusResponse>(
      `/management/licenses/${props.licenseId}/professionals/${feegowProfessionalId.value}/status`
    );

    sessionStorage.setItem(storageKey, JSON.stringify(response));
    sessionStorage.setItem(cachedTimeKey, Date.now().toString());

    hasEnabledSeat.value = response.enabled || response.isVip;

    if (response.enabled) {
      UserTracking.identifyUser({
        pms_name: props.pms || "feegow",
        country_code: "br",
        pms_doctor_id: `${props.licenseId}-${feegowProfessionalId.value}`,
        user_type: "doctor",
      });
    }
  } catch (error) {
    console.error("Error checking seat status:", error);
    hasEnabledSeat.value = false;
  }
};

// =============================================================================
// Event Handlers - Entry Points
// =============================================================================

/**
 * Handle FAB click - main entry point and recovery mechanism
 */
const handleFabClick = (event: Event) => {
  event.stopPropagation();

  // If recording in another tab, bring that tab into focus (or reopen if tab ref was lost)
  if (isRemoteRecording.value) {
    if (isTabOpen.value) {
      focusTab();
    } else if (machine.isRecoveredSession) {
      tryFocusNamedTab();
    } else {
      openTab(machine.recorderUrl);
    }
    return;
  }

  // If in recording state, toggle recorder visibility
  if (machine.isIn("recording")) {
    machine.setRecorderVisible(!machine.recorderVisible);
    return;
  }

  // If in review_ready or processing state and minimized, restore the view
  if ((currentState.value === "review_ready" || currentState.value === "processing") && machine.minimized) {
    machine.restore();
    return;
  }

  // If in processing state, toggle display mode
  if (currentState.value === "processing") {
    machine.setDisplayMode(machine.displayMode === "modal" ? "widget" : "modal");
    return;
  }

  // Track CTA click event
  UserTracking.trackEvent("cta_clicked", {
    action: "start noa notes",
    location: "banner",
  });

  // Send FAB_CLICK event to state machine (only for idle state)
  machine.send({ type: "FAB_CLICK" });
};

// =============================================================================
// Event Handlers - Onboarding (Legacy - simplified)
// =============================================================================

const handleOnboardingClose = () => {
  localStorage.setItem("aiAssistant.onboardingModalShownDate", new Date().toISOString());
  showOnboardingModal.value = false;
};

const handleOnboardingAccept = () => {
  localStorage.setItem("aiAssistant.onboardingModalShown", "true");
  showOnboardingModal.value = false;
  showConsentModal.value = true;
};

// =============================================================================
// Event Handlers - Consent (Legacy - simplified)
// =============================================================================

const handleConsentClose = () => {
  showConsentModal.value = false;
};

const handleConsentAccept = async () => {
  localStorage.setItem("aiAssistant.hasConsented", "true");
  showConsentModal.value = false;

  // Now proceed to template selection via state machine
  machine.send({ type: "FAB_CLICK" });
};

// =============================================================================
// Event Handlers - Template Selection
// =============================================================================

/**
 * Fetch all templates from /templates endpoint (used by form_template_selection).
 * Stores the full payload in sessionStore and sets templates in state machine.
 */
const fetchAllTemplatesForForm = async () => {
  try {
    const response = (await apiClient.get(
      `/licenses/${props.licenseId}/professionals/${feegowProfessionalId.value}/templates`,
    )) as { templates?: Array<{ id: number; description: string; name?: string; sections?: Record<string, string> }> };

    const templates = response.templates || [];
    sessionStore.setAllTemplates(templates);
    machine.setTemplates(templates);

    sessionStore.setSessionInfo({
      licenseId: props.licenseId,
      patientId: props.patientId,
    });
  } catch (error) {
    console.error("[AIAssistant] Failed to fetch all templates:", error);
  }
};

// =============================================================================
// Event Handlers - Form Template Selection
// =============================================================================

const handleFormTemplateSelected = async (templateId: number) => {
  if (!apiClient || !props.licenseId || !feegowProfessionalId.value || !props.patientId) {
    console.error("[AIAssistant] Cannot create session: missing required data");
    return;
  }

  // Guard: prevent duplicate sessions
  if (machine.shouldBlockSessionCreation(Number(props.patientId), Number(props.licenseId))) {
    console.log("[AIAssistant:FormTemplate] Blocked - session already locked:", machine.lockedSessionId);
    return;
  }

  // Save selected template ID
  const { setTemplateId } = useTemplateStore();
  setTemplateId(templateId.toString());

  try {
    const { createSession } = useSessionApi();
    const isAnalyticsBlockedOnClient = await UserTracking.getIsAnalyticsBlocked();
    const sessionData = await createSession(apiClient, {
      licenseId: props.licenseId,
      professionalId: feegowProfessionalId.value,
      patientId: props.patientId,
      templateId: templateId,
      userId: props.userId,
      analyticsDeviceId: UserTracking.getDeviceId(),
      analyticsSessionId: UserTracking.getSessionId(),
      analyticsUserId: UserTracking.getUserId(),
      pageUrl: window.location.href,
      pageCategory: document.title || undefined,
      isAnalyticsBlockedOnClient,
    });
    sessionStore.setReloadTimeline("");
    if (sessionData?.recorderUrl) {
      machine.send({ type: "SESSION_CREATED", session: sessionData });
    } else {
      console.error("[AIAssistant] No recorderUrl in session data");
      machine.send({ type: "CLOSE" });
    }
  } catch (error) {
    console.error("[AIAssistant] Failed to create session from form template:", error);
    machine.send({ type: "CLOSE" });
  }
};

const handleFormTemplateClose = () => {
  machine.send({ type: "CLOSE" });
};

const handleSessionCreated = async (sessionData: SessionData) => {
  if (sessionData?.recorderUrl) {
    // Send SESSION_CREATED event - state machine handles locking internally
    machine.send({ type: "SESSION_CREATED", session: sessionData });
  } else {
    console.error("[AIAssistant] No recorderUrl in session data");
    machine.send({ type: "CLOSE" });
  }
};

// =============================================================================
// Event Handlers - Device Selection
// =============================================================================

const handleDeviceSelectionClose = () => {
  machine.send({ type: "CLOSE" });
};

const handleDeviceSelectDesktop = (url: string) => {
  if (newTabRecorderEnabled.value) {
    const opened = openTab(url);
    if (!opened) {
      globalToasts.value = [
        {
          content: "Não foi possível abrir o gravador. Verifique se popups estão bloqueados no navegador.",
          variant: "error",
          timeout: 6000,
        },
      ];
      return;
    }
  }
  machine.send({ type: "DEVICE_DESKTOP", recorderUrl: url, isTabRecorder: newTabRecorderEnabled.value });
};

const handleDeviceSelectMobile = () => {
  // Mobile selection shows QR code in the modal, no state transition needed
};

// =============================================================================
// Event Handlers - Recorder
// =============================================================================

const handleRecorderClose = () => {
  // Don't reset state machine - just hide the modal
  // This allows the user to recover via FAB click
  machine.setRecorderVisible(false);
};

/**
 * Handle starting a new session from the recorder modal.
 * This is called when the user clicks "Iniciar nova sessão" in the recorder's dot menu.
 * The session has already been abandoned by the modal component.
 */
const handleStartNewSessionFromRecorder = () => {
  console.log("[AIAssistant] Starting new session from recorder");

  disconnectAllTransports();
  stopStatusPolling();
  sessionStore.clearSessionInfo();

  // Reset iframe states
  resetIframeStates();

  // Reset state machine and go to template selection
  machine.reset();

  // Use nextTick to ensure state is reset before transitioning
  nextTick(() => {
    machine.send({ type: "FAB_CLICK" });
  });
};

const handleRecordingFinished = () => {
  void recorderLifecycleTelemetry.markRecordingFinished();
  // WebSocket will send WS_RECORDED event to transition to processing
  machine.setDisplayMode("widget");
};

const onRecorderOpened = () => {
  void recorderLifecycleTelemetry.emit("modal_opened");
};

const onRecorderClosed = () => {
  void recorderLifecycleTelemetry.emit("modal_hidden");
  machine.setRecorderVisible(false);
};

const onRecorderReady = () => {
  void recorderLifecycleTelemetry.emit("recorder_ready");
};

const onRecordingStarted = () => {
  void recorderLifecycleTelemetry.markRecordingStarted("iframe_post_message");
};

const onIframeLoad = () => {
  if (iframeLoadTimeout.value) {
    clearTimeout(iframeLoadTimeout.value);
    iframeLoadTimeout.value = null;
  }
  recorderLoaded.value = true;
  void recorderLifecycleTelemetry.emit("iframe_loaded");
};

const onIframeLoadStart = () => {
  recorderLoading.value = true;
  void recorderLifecycleTelemetry.emit("iframe_load_started");
};

const onIframeError = (event: Event) => {
  console.error("Recorder iframe failed to load:", event);
  if ((event as any).type === "retry") {
    resetIframeStates();
    return;
  }
  recorderError.value = "Erro ao carregar o gravador. Tente novamente.";
  void recorderLifecycleTelemetry.emit("iframe_error", {
    metadata: { eventType: event.type || "error" },
  });
};

const resetIframeStates = () => {
  if (isResettingIframe.value) return;

  isResettingIframe.value = true;
  void recorderLifecycleTelemetry.emit("iframe_recreated");
  nextTick(() => {
    if (iframeLoadTimeout.value) {
      clearTimeout(iframeLoadTimeout.value);
      iframeLoadTimeout.value = null;
    }
    recorderLoaded.value = false;
    recorderLoading.value = true;
    recorderError.value = null;
    recorderKey.value = Date.now();
    setTimeout(() => {
      isResettingIframe.value = false;
    }, 100);
  });
};

// =============================================================================
// Event Handlers - Review Popover
// =============================================================================

/**
 * Handle minimize - soft touch that hides the view but keeps state intact
 * FAB will show to allow user to restore the view
 */
const onMinimize = () => {
  machine.minimize();
  // Don't change displayMode - preserve it for when user restores
  // Don't change state machine - this is purely a UI visibility toggle
};

const onReviewSaved = async () => {
  // Save completed - show toast immediately (before WebSocket triggers unmount)
  globalToasts.value = [
    {
      content: "Resumo salvo com sucesso.",
      variant: "default",
      timeOut: 3000,
    },
  ];
};

const onSessionDiscarded = () => {
  // User explicitly discarded the session - reset state machine
  // Use nextTick to ensure all current event handlers complete before state change
  // This prevents "emitsOptions" error from components being unmounted mid-event
  nextTick(() => {
    machine.send({ type: "WS_VERIFIED" });
  });
};

/**
 * Handle closing the empty summary modal
 * Resets the state machine to idle and clears the session lock
 */
const handleEmptySummaryClose = () => {
  console.log("[AIAssistant:EmptySummaryModal] handleEmptySummaryClose called - resetting state machine");
  machine.reset(); // Unlocks session and returns to idle
};

const handleInterruptedSessionClose = () => {
  showInterruptedSessionModal.value = false;
};

const handleCopyTranscription = async () => {
  try {
    await navigator.clipboard.writeText(transcriptionTextContent.value);
    transcriptionToasts.value = [
      {
        content: "Texto copiado com sucesso!",
        variant: "default",
        timeOut: 3000,
      },
    ];
  } catch (err) {
    console.error("Failed to copy text:", err);
  }
};

// =============================================================================
// Check for Existing Session
// =============================================================================

const checkForExistingSession = async () => {
  if (!apiClient || !props.licenseId || !feegowProfessionalId.value || !props.patientId) {
    return;
  }

  try {
    const settings = (await apiClient.get(
      `/licenses/${props.licenseId}/professionals/${feegowProfessionalId.value}/settings`,
      { patientId: props.patientId }
    )) as { templates?: any[]; session?: any; threadCount?: number };

    sessionStore.setSessionInfo({
      licenseId: props.licenseId,
      patientId: props.patientId,
    });

    // Cache templates for later use
    const templates = settings.templates || [];
    if (templates.length > 0) {
      machine.setTemplates(templates);
      sessionStore.setThreadCount(settings.threadCount || 0);
    }

    if (!settings.session) {
      return;
    }

    // Extract session data
    const session = settings.session;
    const status = (session?.status || "").toLowerCase();

    // Don't restore completed sessions (user_interrupted = tab closed mid-recording)
    const completedStatuses = ["verified", "discarded", "error", "user_interrupted"];
    if (completedStatuses.includes(status)) {
      return;
    }

    // Only restore in-process sessions when new tab recorder feature is enabled
    if (status !== "summarized" && !newTabRecorderEnabled.value) {
      return;
    }

    // Interrupted: recorder connection was lost — show informational modal, don't restore
    if (status === "interrupted") {
      showInterruptedSessionModal.value = true;
      return;
    }

    // For summarized status, fetch full session data
    let fullSessionData = session;
    if (session.recorderSessionId) {
      try {
        const fetchedData = (await apiClient.get(`/session/${session.recorderSessionId}`)) as any;
        fullSessionData = { ...session, ...fetchedData };
      } catch {
        // Could not fetch session data, use existing
      }
    }

    // Send EXISTING_SESSION event to state machine
    machine.send({
      type: "EXISTING_SESSION",
      session: fullSessionData,
      status,
    });

    // Re-attach to the recorder tab after page reload.
    // window.open("", name) for an *existing* named window is not a popup — returns
    // reference silently. Popup blockers only fire when the tab doesn't exist (browser
    // tries to create a new blank tab), so null here means "tab is gone".
    if (["recording", "recorded", "transcribing", "transcribed", "analizing", "processing"].includes(status)) {
      watchNamedTab();
    }

    // Connect WebSocket immediately so status updates arrive without waiting
    // for Vue's reactive watcher cycle (critical for in-progress sessions)
    if (fullSessionData.recorderSessionId) {
      connectWebSocket(fullSessionData.recorderSessionId);
    }
  } catch (error) {
    console.error("[AIAssistant] Error checking session:", error);
  }
};

// =============================================================================
// CTA Button Management
// =============================================================================

let noaButtonApp: any = null;
let noaButtonMountNode: HTMLElement | null = null;
let observer: MutationObserver | null = null;

function removeAttendanceStartButton() {
  if (noaButtonApp && noaButtonMountNode) {
    noaButtonApp.unmount();
    noaButtonMountNode.remove();
    noaButtonApp = null;
    noaButtonMountNode = null;
  }
}

function refreshStartAttendanceButton() {
  const ulMenu = document.getElementById("lMenu");
  const liTrayBin = ulMenu?.querySelector("li.row.sidebar-stat.tray-bin");
  const divContador = liTrayBin?.querySelector("#divContador");
  if (!divContador) return;

  const startBtn = divContador.querySelector("button.btn-acao-atendimento.btn-success");
  attendanceStarted.value = !startBtn;

  if (attendanceStarted.value || !hasEnabledSeat.value) {
    removeAttendanceStartButton();
    return;
  }

  const buttonText = startBtn ? "Iniciar com Anotador de IA" : "Iniciar Anotador de IA";
  const existingNoaButton = divContador.querySelector(".noa-feegow-pms-btn");

  if (existingNoaButton) {
    if (existingNoaButton.textContent?.trim() === buttonText) return;
    removeAttendanceStartButton();
  }

  const skeletonContainer = divContador.querySelector(".fc-skeleton-buttons");
  if (skeletonContainer) skeletonContainer.remove();

  noaButtonMountNode = document.createElement("div");
  divContador.appendChild(noaButtonMountNode);

  noaButtonApp = createApp({
    render() {
      return h(AIAssistantAttendanceStartCTA, {
        targetSelector: ".btn-acao-atendimento",
        buttonText: buttonText,
        onClick: () => {
          machine.send({ type: "FAB_CLICK" });
        },
      });
    },
  });
  noaButtonApp.use(WatsonComponentsCompatible);
  noaButtonApp.mount(noaButtonMountNode);
}

// =============================================================================
// Watchers
// =============================================================================

// Watch for seat status changes
watch(hasEnabledSeat, async (newValue) => {
  if (newValue) {
    await checkForExistingSession();
    if (showNoaButton.value) {
      refreshStartAttendanceButton();
    }

    // Auto-start if configured
    if (props.config?.autoStart === true && machine.isIn("idle")) {
      setTimeout(() => machine.send({ type: "FAB_CLICK" }), 500);
    }
  } else {
    removeAttendanceStartButton();
  }
});

// Watch state changes for CTA button, template fetching, and display mode
watch(
  machine.stateRef,
  async (newState, oldState) => {
    // =========================================================================
    // CRITICAL: All reactive state updates must happen SYNCHRONOUSLY here
    // before any await statements to prevent intermediate renders
    // =========================================================================

    // Debug logging for error state
    console.log(`[AIAssistant:StateWatcher] State changed: ${oldState} → ${newState}`);
    if (newState === "error") {
      console.log("[AIAssistant:StateWatcher] ERROR STATE ENTERED!", {
        errorMessage: machine.errorMessage,
        sessionData: machine.sessionData,
      });
    }

    // Sync local currentState ref for template reactivity
    currentState.value = newState;

    // Note: displayMode, minimized, and recorderVisible are now managed by
    // useStateMachine in the send() function during state transitions.
    // Additional UI state updates can be done here if needed.

    // Update displayMode for transitions not handled by state machine
    if (newState === "processing" && oldState === "recording") {
      machine.setDisplayMode("modal");
    } else if (newState === "idle") {
      machine.setDisplayMode("widget");
      machine.setRecorderVisible(true); // Reset for next use
      machine.restore(); // Reset minimize state
    }

    // Update recorder visibility
    if (newState === "recording") {
      machine.setRecorderVisible(true);
    } else if (oldState === "recording" && newState !== "recording") {
      machine.setRecorderVisible(false);
    }

    // =========================================================================
    // Below this point: Non-critical operations (can be async)
    // =========================================================================

    // Fetch templates when entering template selection
    if (newState === "form_template_selection") {
      if (sessionStore.getAllTemplates().length === 0) {
        await fetchAllTemplatesForForm();
      } else {
        machine.setTemplates(sessionStore.getAllTemplates());
      }
    }

    // Update CTA button visibility (DOM manipulation, not reactive)
    if (newState === "review_ready" || newState === "processing") {
      removeAttendanceStartButton();
    } else if (newState === "idle" && showNoaButton.value) {
      refreshStartAttendanceButton();
    }
  },
  { immediate: true },
);

// Watch recorder URL for timeout
watch(
  () => machine.recorderUrl,
  (newUrl, oldUrl) => {
    if (newUrl && newUrl !== oldUrl && !isResettingIframe.value) {
      resetIframeStates();
      nextTick(() => {
        iframeLoadTimeout.value = setTimeout(() => {
          if (!recorderLoaded.value) {
            recorderError.value = "Timeout ao carregar o gravador. Tente novamente.";
          }
        }, 10000);
      });
    }
  }
);

// Watch for patient/license context changes
watch(
  () => [props.patientId, props.licenseId],
  ([newPatient, newLicense], [oldPatient, oldLicense]) => {
    const patientChanged = oldPatient && newPatient !== oldPatient;
    const licenseChanged = oldLicense && newLicense !== oldLicense;

    if (patientChanged || licenseChanged) {
      machine.reset();
    }
  }
);

// Watch for prop changes to update GrowthBook attributes
watch(
  () => ({ userId: props.userId, licenseId: props.licenseId, professionalId: props.professionalId }),
  (newContext) => {
    setAttributes({
      userId: newContext.userId || undefined,
      licenseId: newContext.licenseId || undefined,
      professionalId: newContext.professionalId || undefined,
    });
  },
  { immediate: false }
);

// Watch for transport connection changes - start polling on disconnect
watch(transportConnected, (isConnected, wasConnected) => {
  // Start polling fallback on disconnect during active flow
  if (wasConnected && !isConnected && (machine.isIn("recording") || machine.isIn("processing"))) {
    console.log("[AIAssistant] Transport disconnected, starting polling fallback");
    startStatusPolling();
  }
  // Polling is stopped via onRoomJoined callback once socket confirms room membership
});

// Watch for transport connection failure to start polling fallback
watch(transportConnectionFailed, (failed) => {
  if (failed && (machine.isIn("recording") || machine.isIn("processing"))) {
    console.log("[AIAssistant] Transport connection failed, starting polling fallback");
    startStatusPolling();
  }
});

// Also check when entering recording/processing if transport already failed
watch(
  () => machine.stateRef.value,
  (newState) => {
    if (
      (newState === "recording" || newState === "processing") &&
      transportConnectionFailed.value &&
      !statusPollingIntervalId.value
    ) {
      console.log("[AIAssistant] Entered active state with failed transport connection, starting polling fallback");
      startStatusPolling();
    }
  }
);

// Watch sessionId to connect appropriate transport based on feature flag
watch(
  () => machine.sessionDataRef.value?.recorderSessionId,
  (newSessionId, oldSessionId) => {
    if (newSessionId && newSessionId !== oldSessionId) {
      if (useLongPollingFlag.value) {
        disconnectWebSocket();
        if (updateLpCallback) {
          updateLpCallback(handleWebSocketStatusChange);
        }
        connectLongPolling(newSessionId);
      } else {
        disconnectLongPolling();
        if (updateStatusChangeCallback) {
          updateStatusChangeCallback(handleWebSocketStatusChange);
        }
        connectWebSocket(newSessionId);
      }
    }
  },
  { immediate: true }
);

// Watch template selection visibility - fires modal_shown on first appearance
watch(showFormTemplateSelection, (isVisible) => {
  if (isVisible) {
    UserTracking.trackEvent("modal_shown", {
      modal_name: "initiate noa notes",
      page_category: "patients",
      page_section: "patient profile",
      page_type: "",
      modal_type: "personalized_template",
      step_name: "Template Selection",
    });
  }
});

// Watch device selection visibility - fallback for auto-bypass scenarios
watch(showDeviceSelection, (isVisible) => {
  if (isVisible) {
    UserTracking.trackEvent("modal_shown", {
      modal_name: "initiate noa notes",
      page_category: "patients",
      page_section: "patient profile",
      page_type: "",
      step_name: "Device Selection",
    });
  }
});

// Clear recorder tab reference when leaving recording state
watch(currentState, (newState, oldState) => {
  if (oldState === "recording" && newState !== "recording") {
    clearTab();
  }
});

// Detect recorder tab closed unexpectedly by the user
watch(tabClosedUnexpectedly, (closed) => {
  if (closed) {
    const recorderSessionId = machine.sessionData?.recorderSessionId;
    if (recorderSessionId) {
      apiClient.put(`/session/${recorderSessionId}/user-interrupt`).catch(() => {
        // Fire-and-forget: UI feedback and state reset proceed regardless of API result
      });
    }

    globalToasts.value = [
      {
        content: "O gravador foi fechado de forma inesperada. Em até 1h, o resumo da sua transcrição estará disponível nessa aba.",
        variant: "error",
        timeout: 12000,
      },
    ];
    machine.send({ type: "CLOSE" });
  }
});

const handleFinalizeRecording = () => {
  if (isTabOpen.value) {
    focusTab();
  } else if (machine.isRecoveredSession) {
    tryFocusNamedTab();
  } else {
    openTab(machine.recorderUrl);
  }
};

// =============================================================================
// Lifecycle
// =============================================================================

onMounted(async () => {
  if (showNoaButton.value) {
    refreshStartAttendanceButton();
    const ulMenu = document.getElementById("lMenu");
    const liTrayBin = ulMenu?.querySelector("li.row.sidebar-stat.tray-bin");
    const divContador = liTrayBin?.querySelector("#divContador");
    if (divContador) {
      observer = new MutationObserver(() => refreshStartAttendanceButton());
      observer.observe(divContador, { childList: true, subtree: true });
    }
  }

  await checkSeatStatus();
});

onUnmounted(() => {
  clearTab();
  void recorderLifecycleTelemetry.emitUnmountedIfRecording({ keepalive: true });
  recorderLifecycleTelemetry.cleanup();
  clearNoaRumSessionContext();
  if (observer) observer.disconnect();
  removeAttendanceStartButton();
  if (iframeLoadTimeout.value) {
    clearTimeout(iframeLoadTimeout.value);
  }
  disconnectAllTransports();
  stopStatusPolling();
  sessionStore.clearSessionInfo();
});

// Expose for debugging
if (isLocalEnv() || isStagingEnv()) {
  (window as any).UserTracking = UserTracking;
  (window as any).aiStateMachine = machine;
  (window as any).aiSessionStore = sessionStore;

  // Expose UI state for DevPanel (deprecated - StateTab should use machine directly)
  (window as any).__aiAssistantUIState = {
    get displayMode() {
      return machine.displayMode;
    },
    get isMinimized() {
      return machine.isUIMinimized;
    },
    get showFab() {
      return showFab.value;
    },
    get recorderVisible() {
      return machine.isIn("recording") && machine.recorderVisible;
    },
    get popoverVisible() {
      return machine.isIn("review_ready", "processing");
    },
    get processingModalVisible() {
      return machine.isIn("processing") && machine.displayMode === "modal";
    },
    get reviewModalVisible() {
      return machine.isIn("review_ready") && machine.displayMode === "modal";
    },
    get currentState() {
      return machine.state;
    },
  };
}

// Provide for child components
provide("openTranscriptionModal", (url: string) => {
  machine.send({ type: "DEVICE_DESKTOP", recorderUrl: url });
});
provide("closeTranscriptionModal", () => machine.send({ type: "CLOSE" }));
provide("closePopover", () => machine.send({ type: "CLOSE" }));
provide("stateMachine", machine);
</script>
